import {
    environment,
    useThrottleFn,
    useDebounceFn,
    useLockFn,
    uploadFile,
    previewPicture,
    copyToClipboard,
    getImgInfo,
    LucaResizeObserver,
    getClientRectInfo,
    isWeApp,
    addEventCenterOfOnce,
} from './tools';
import { handleTokenExample } from './class-example';

export {
    environment,
    useThrottleFn,
    useDebounceFn,
    useLockFn,
    uploadFile,
    previewPicture,
    copyToClipboard,
    getImgInfo,
    LucaResizeObserver,
    getClientRectInfo,
    isWeApp,
    addEventCenterOfOnce,
};

// 将字符串转换为驼峰命名格式
export const camelCase = (str: string) => {
    if (typeof str !== 'string') return '';
    // 将字符串分割成单词数组
    const words = str.split('_');
    if (words.length <= 1) return str;
    // 转换为驼峰格式
    const camelCaseStr = words
        .map((word, index) => {
            if (index === 0) {
                return word.toLowerCase(); // 第一个单词保持小写
            }
            return word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase(); // 后续单词首字母大写
        })
        .join('');
    return camelCaseStr;
};

// 生成uuid
export const getUuid = () => {
    let d = new Date().getTime();
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        d += performance.now();
    }
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uuid;
};

// 对象中的key转换为小驼峰
export const convertKeysToCamelCase = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(v => convertKeysToCamelCase(v));
    } else if (obj?.constructor === Object) {
        return Object.keys(obj).reduce((result, key) => {
            // console.log('convertKeysToCamelCase', key);
            const camelCaseKey = camelCase(key);
            // console.log('camelCaseKey', camelCaseKey);
            result[camelCaseKey] = convertKeysToCamelCase(obj[key]);
            return result;
        }, {} as any);
    }
    return obj;
};

export const parseSearchString = (search: string): Record<string, string> => {
    if (!search) return {};
    let newSearch = search;
    if (search.startsWith('?')) {
        newSearch = search.substring(1);
    }
    const params: any = {};
    const pairs = newSearch.split('&');
    for (const pair of pairs) {
        const [key, value] = pair.split('=');
        params[key] = value;
    }
    return params;
};

export const formatTime = (linkTime: string | number | Date, format: string) => {
    const date = new Date(linkTime);
    const placeholders: { [key: string]: number | string } = {
        YYYY: date.getFullYear(),
        MM: String(date.getMonth() + 1).padStart(2, '0'), // Months are zero indexed
        DD: String(date.getDate()).padStart(2, '0'),
        HH: String(date.getHours()).padStart(2, '0'),
        mm: String(date.getMinutes()).padStart(2, '0'),
        ss: String(date.getSeconds()).padStart(2, '0'),
    };
    return format.replace(/YYYY|MM|DD|HH|mm|ss/g, match => placeholders[match] as string);
};

export const formatSeconds = (val: number): string => {
    // 计算时、分、秒
    const hours = Math.floor(val / 3600);
    const time = val * 1000;
    if (hours) return formatTime(time, 'HH:mm:ss');
    return formatTime(time, 'mm:ss');
};

export const delay = (waitTime: number) => {
    return new Promise(resolve => setTimeout(resolve, waitTime));
};

export const delayExcuteCallback = (callback: () => void, waitTime: number) => {
    setTimeout(callback, waitTime);
};

export const throttle = (func: () => void, limit: number) => {
    let lastTime = 0;
    return (...args: any) => {
        const now = Date.now();
        if (now - lastTime >= limit) {
            lastTime = now;
            func.apply(this, args);
        }
    };
};

export const transformOptions = async (options: any): Promise<any> => {
    const copyOptions = { ...options };
    let newOptions: any = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json;charset=UTF-8',
        },
        timeout: 10000,
    };
    if (copyOptions.isForm) {
        newOptions.headers!['Content-Type'] = 'application/x-www-form-urlencoded';
        delete copyOptions.isForm;
    }
    if (copyOptions.withoutToken) {
        delete copyOptions.withoutToken;
    } else {
        const accessToken = await handleTokenExample.getFronttoken();
        newOptions.headers!['Authentication'] = `Basic ${accessToken}`;
    }
    if (copyOptions.headers) {
        newOptions.headers = {
            ...newOptions.headers,
            ...copyOptions.headers,
        };
        delete copyOptions.headers;
    }
    newOptions = { ...newOptions, ...copyOptions };
    // 注意：小程序中请求头参数为header, 而非Axios中的headers
    if (isWeApp) {
        newOptions.header = newOptions.headers;
        delete newOptions.headers;
    }
    // console.log('newOptions', newOptions);
    return newOptions;
};

// 将对象中的指定key的value值转换为数字
export const transformValueToNumberOrBooleanOfObj = (obj: Record<string, any>, keys: string[]): any => {
    try {
        if (typeof obj !== 'object' || Array.isArray(obj)) return {};
        const newObj = { ...obj };
        keys.forEach(key => {
            let value = obj[key];
            if (value === 'true') value = true;
            if (value === 'false') value = false;
            if (+value) value = +value;
            newObj[key] = value;
        });
        return newObj;
    } catch (error) {
        return {};
    }
};

/**
 * 根据图片尺寸生成阿里云OSS压缩参数，目标控制在100k左右
 * @param width 图片宽度
 * @param height 图片高度
 * @returns OSS处理参数字符串
 */
export const generateOssParams = (width: number, height: number): string => {
    // 计算图片像素总数
    const pixels = width * height;

    let quality = 80; // 默认质量
    let resizePercent = 100; // 默认缩放比例

    // 根据像素数量调整压缩参数
    if (pixels <= 200000) {
        // 小于20万像素 (约448x448)
        quality = 85;
        resizePercent = 100;
    } else if (pixels <= 500000) {
        // 20-50万像素 (约707x707)
        quality = 75;
        resizePercent = 90;
    } else if (pixels <= 1000000) {
        // 50-100万像素 (约1000x1000)
        quality = 65;
        resizePercent = 80;
    } else if (pixels <= 2000000) {
        // 100-200万像素 (约1414x1414)
        quality = 55;
        resizePercent = 70;
    } else if (pixels <= 4000000) {
        // 200-400万像素 (约2000x2000)
        quality = 45;
        resizePercent = 60;
    } else if (pixels <= 8000000) {
        // 400-800万像素 (约2828x2828)
        quality = 35;
        resizePercent = 50;
    } else {
        // 大于800万像素
        quality = 25;
        resizePercent = 40;
    }

    // 对于特别宽或特别高的图片，额外调整
    const aspectRatio = Math.max(width, height) / Math.min(width, height);
    if (aspectRatio > 3) {
        // 长条形图片，适当增加压缩
        quality = Math.max(quality - 10, 20);
    }

    return `?x-oss-process=image/format,jpg/quality,Q_${quality}/resize,p_${resizePercent}`;
};
